import { css } from '@emotion/react';
import { parseSections, WPM_DEFAULT } from '@sonematic/shared';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { RehearsalView } from '@/components/rehearsal/rehearsal-view';
import { Spinner } from '@/components/ui/spinner';
import { fetchProject } from '@/lib/api-client';
import type { ProjectWithSections } from '@/lib/api-client';
import { toFriendlyError } from '@/lib/utils/error-handler';
import { darkTheme } from '@/theme/dark-theme';
import { spacing } from '@/theme/tokens';

export function ProjectRehearse() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const [project, setProject] = useState<ProjectWithSections | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchProject(id)
      .then((projectData) => {
        if (cancelled) return;
        setProject(projectData);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(toFriendlyError(err).message);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const sections = useMemo(() => {
    if (!project) return [];

    // Always parse rawContent for the latest text — DB sections
    // may be stale if the user edited after the last analysis.
    if (project.rawContent) {
      return parseSections(project.rawContent).sections.map((s) => ({
        title: s.title,
        content: s.content,
        position: s.position,
        wordCount: s.wordCount,
        speakerNotes: s.speakerNotes,
      }));
    }

    return [];
  }, [project]);

  const filteredSections = useMemo(() => {
    if (sectionParam === null) return sections;
    const pos = parseInt(sectionParam, 10);
    if (isNaN(pos)) return sections;
    const match = sections.filter((s) => s.position === pos);
    return match.length > 0 ? match : sections;
  }, [sections, sectionParam]);

  if (loading) {
    return (
      <div
        css={css`
          min-height: 100vh;
          background: ${darkTheme.background};
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <Spinner size="md" color={darkTheme.textSecondary} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        css={css`
          min-height: 100vh;
          background: ${darkTheme.background};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: ${darkTheme.error};
          font-size: 18px;
          gap: ${spacing.md};
        `}
      >
        <p>{error}</p>
        <Link
          to="/app/projects"
          css={css`
            color: ${darkTheme.primary};
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 4px;

            &:hover {
              text-decoration: underline;
            }
          `}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <RehearsalView
      sections={filteredSections}
      globalWpm={project?.globalWpm ?? WPM_DEFAULT}
      projectId={id!}
      title={project?.title}
    />
  );
}
